import {BehaviorSubject, Observable} from 'rxjs';
import {filter, withLatestFrom} from 'rxjs/operators';
import {NcDocumentFormDialogComponent} from '../nc-document-form-dialog/nc-document-form-dialog.component';
import {Injectable} from '@angular/core';
import {Option} from 'nc-shared';
import {DialogService} from 'nc-utils';
import {DocumentModel} from '../models/document-model';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {DocumentValidators} from '../validators/document-validators';
import {TableData} from 'nc-datatable';

@Injectable({
  providedIn: 'root'
})
export class NcDocumentsService {

  constructor(private dialogService: DialogService, private formBuilder: FormBuilder) {
  }

  initialize(formArray: FormArray, data$: BehaviorSubject<TableData>): void {
    this.updateTable(data$, formArray);

    formArray.valueChanges.subscribe(() => {
      this.updateTable(data$, formArray);
    });
  }

  populateData(formArray: FormArray, documents: DocumentModel [], isReadOnly = false): void {
    formArray.clear();
    const totalSize = documents.reduce((a, b) => a + (b.file?.size ?? 0), 0);

    for (let i = 0; i < documents?.length; i++) {
      const document = documents[i];
      const names = documents.map(x => x.name.toLowerCase()).filter(name => name !== document?.name.toLowerCase());
      formArray.push(this.createExistingFormGroup(document, totalSize, names, i, isReadOnly));
    }
  }

  delete(model: DocumentModel, formArray: FormArray): void {
    const index = formArray.getRawValue().findIndex(x => x.tableId === model.tableId);
    formArray.removeAt(index);
  }

  openDialog(model: DocumentModel, formArray: FormArray, documentTypes$: Observable<Option []>): void {
    const formGroup = this.getFormGroup(formArray, model);

    const dialogConfig = {
      width: '500px',
      data: {
        formGroup,
        documentTypes$,
        formArray
      },
    };

    const dialogRef = this.dialogService.open(NcDocumentFormDialogComponent, dialogConfig);

    dialogRef.afterClosed().pipe(
      filter(result => result?.success),
      withLatestFrom(documentTypes$)
    ).subscribe(([result, documentTypes]) => {
      const selectedType = documentTypes.find(value => value.value === formGroup.get('documentType').value);

      formGroup.get('documentTypeTranslation').patchValue(selectedType.text);

      if (!model) {
        formArray.push(formGroup);
      } else {
        const index = formArray.getRawValue().findIndex(x => x.tableId === model.tableId);
        formArray.removeAt(index);
        formArray.insert(index, formGroup);
      }
    });
  }

  getDocuments(formGroup: FormGroup): Array<DocumentModel> {
    const documents = formGroup.get('documents').value;
    return documents.map(x => {
      const file = x.file ? x.file.files[0] : null;
      return {
        ...x,
        file
      };
    });
  }

  private updateTable = (data$: BehaviorSubject<TableData>, formArray: FormArray) => {
    const tableData = {records: []};

    for (let i = 0; i < formArray.length; i++) {
      const formGroup = formArray.at(i) as FormGroup;
      const rawValue = formGroup.getRawValue();

      tableData.records.push(this.formatDataForTable(rawValue, formGroup.invalid));
    }

    data$.next(tableData);
  }

  private getFormGroup = (formArray: FormArray, model: DocumentModel): FormGroup => {
    let formGroup: FormGroup;
    const documents = formArray.getRawValue() as DocumentModel[];
    const names = documents.map(document => document.name.toLowerCase()).filter(name => name !== model?.name.toLowerCase());
    const totalSize = documents.reduce((a, b) => a + (b.file?.size ?? 0), 0);

    if (model) {
      const index = formArray.getRawValue().findIndex(x => x.tableId === model.tableId);
      const existing = formArray.at(index) as FormGroup;
      formGroup = this.createExistingFormGroup(existing.getRawValue(), totalSize, names, index);
    } else {
      const tableId = formArray.length === 0 ? 0 : formArray.at(formArray.length - 1).get('tableId').value + 1;
      formGroup = this.createNewFormGroup(tableId, totalSize, names);
    }

    return formGroup;
  }

  private createNewFormGroup = (tableId: number, totalSize: number, names: Array<string>): FormGroup => {
    return this.formBuilder.group({
      id: [0],
      tableId: [tableId],
      name: ['', [Validators.required, DocumentValidators.uniqueNameValidator(names)]],
      extension: [''],
      nameWithExtension: [''],
      docmanId: [0],
      documentTypeTranslation: [''],
      documentType: ['', [Validators.required]],
      file: [null, [
        Validators.required,
        DocumentValidators.fileSizeValidator(),
        DocumentValidators.totalFilesSizeValidator(totalSize, false)],
      ],
      uploadedOn: [''],
      uploadedBy: [''],
      comment: ['']
    });
  }

  private createExistingFormGroup = (
    document: DocumentModel,
    totalSize: number,
    names: Array<string>,
    tableId?: number,
    isReadOnly = false
  ): FormGroup => {
    const nameValidators = isReadOnly ? null : [Validators.required, DocumentValidators.uniqueNameValidator(names)];
    const typeValidators = isReadOnly ? null : [Validators.required];
    const fileValidators = isReadOnly ? null : [
      DocumentValidators.fileSizeValidator(),
      DocumentValidators.totalFilesSizeValidator(totalSize, false)
    ];
    return this.formBuilder.group({
      id: [document.id],
      tableId: [tableId ?? document.tableId],
      name: [document.name, nameValidators],
      nameWithExtension: [document.nameWithExtension],
      extension: [document.extension],
      documentTypeTranslation: [document.documentTypeTranslation],
      docmanId: [document.docmanId],
      documentType: [document.documentType, typeValidators],
      file: [document.file, fileValidators],
      uploadedOn: [document.uploadedOn],
      uploadedBy: [document.uploadedBy],
      comment: [document.comment]
    });
  }

  private formatDataForTable = (model: DocumentModel, isInvalid: boolean) => {
    return {
      ...model,
      isInvalid
    };
  }
}
